import * as BABYLON from '@babylonjs/core';
import { ShadowOnlyMaterial } from '@babylonjs/materials';
import * as GUI from '@babylonjs/gui';
import { ViewerTransformType } from './furniture';

export function createLights(scene: BABYLON.Scene): BABYLON.ShadowGenerator {
    const keyLightPosition = new BABYLON.Vector3(-1.5, 1.5, 1.5);
    const fillLightPosition = new BABYLON.Vector3(1.5, 1.5, 1.5);
    const backLightPosition = new BABYLON.Vector3(-1.5, 1.5, -1.5);
    const focalPoint = new BABYLON.Vector3(0, 0.5, 0);

    const keyLight = new BABYLON.DirectionalLight(
        'key-light',
        focalPoint.subtract(keyLightPosition).normalize(),
        scene,
    );
    keyLight.position = keyLightPosition;

    keyLight.intensity = 15;
    keyLight.shadowMaxZ = 3;
    keyLight.radius = 0.2;

    const fillLight = new BABYLON.SpotLight(
        'fill-light',
        fillLightPosition,
        focalPoint.subtract(fillLightPosition).normalize(),
        Math.PI / 2,
        2,
        scene,
    );
    fillLight.intensity = 8;
    fillLight.radius = 0.4;

    const backLight = new BABYLON.SpotLight(
        'back-light',
        backLightPosition,
        focalPoint.subtract(backLightPosition).normalize(),
        Math.PI / 2,
        2,
        scene,
    );
    fillLight.intensity = 4;
    fillLight.radius = 0.4;

    const globalLight = new BABYLON.HemisphericLight('global-light', BABYLON.Vector3.Zero(), scene);
    globalLight.intensity = 0.1;

    const shadowGenerator = new BABYLON.ShadowGenerator(1024, keyLight);
    shadowGenerator.bias = 0.0015;
    shadowGenerator.useBlurCloseExponentialShadowMap = true;
    return shadowGenerator;
}

export function createGround(scene: BABYLON.Scene): BABYLON.Mesh {
    const groundMesh = BABYLON.MeshBuilder.CreatePlane('ground', { size: 20 }, scene);
    groundMesh.receiveShadows = true;
    const groundMat = new ShadowOnlyMaterial('ground-material', scene);
    groundMat.backFaceCulling = false;
    groundMat.alpha = 0.03;

    groundMesh.material = groundMat;
    groundMesh.rotation = new BABYLON.Vector3(Math.PI / 2, 0, 0);
    groundMesh.position = new BABYLON.Vector3(0, 0, 10);
    groundMesh.freezeWorldMatrix(); // since we are not going to be moving the ground, we freeze it for better performance

    return groundMesh;
}

export function createWall(scene: BABYLON.Scene): BABYLON.Mesh {
    const wallMesh = BABYLON.MeshBuilder.CreatePlane('wall', { size: 20 }, scene);
    wallMesh.receiveShadows = true;
    const wallMat = new ShadowOnlyMaterial('wall-material', scene);
    wallMat.backFaceCulling = true;
    wallMat.alpha = 0.03;

    wallMesh.material = wallMat;
    wallMesh.rotation = new BABYLON.Vector3(0, Math.PI, 0);
    wallMesh.position = new BABYLON.Vector3(0, 10, 0.0);
    wallMesh.freezeWorldMatrix(); // since we are not going to be moving the wall, we freeze it for better performance

    return wallMesh;
}

export const createArcRotateCamera = (
    canvas: HTMLCanvasElement,
    scene: BABYLON.Scene,
    onSceneUserInteraction: () => void,
    panToModelOnSelection: boolean,
) => {
    const startAlpha = Math.PI / 4;
    const startBeta = Math.PI / 2.6;
    const startRadius = 2;
    const startPosition = new BABYLON.Vector3(0, 0.5, 0);
    const camera = new BABYLON.ArcRotateCamera(
        'camera',
        startAlpha,
        startBeta,
        startRadius,
        startPosition,
        scene,
        true,
    );
    camera.attachControl(canvas, false);

    // Set some basic camera settings
    camera.minZ = 0.01; // clip at 1 meter

    camera.panningSensibility = 500; // how fast do you pan, set to 0 if you don't want to allow panning
    camera.panningInertia = 0;

    camera.panningOriginTarget = BABYLON.Vector3.Zero(); // where does the panning distance limit originate from
    camera.panningDistanceLimit = 1.5; // how far can you pan from the origin

    camera.wheelPrecision = 250;
    camera.allowUpsideDown = false; // don't allow zooming inverted
    camera.lowerRadiusLimit = 0.2; // how close can you zoom
    camera.upperRadiusLimit = 100; // how far out can you zoom
    camera.lowerBetaLimit = 0.5; // how high can you move the camera
    camera.upperBetaLimit = 1.6; // how low down can you move the camera
    camera.inertia = 0.95;

    camera.checkCollisions = true; // make the camera collide with meshes
    camera.collisionRadius = new BABYLON.Vector3(2, 2, 2); // how close can the camera go to other meshes

    camera.pinchPrecision = 300; // set pinch zoom speed to something reasonable for the scene

    // prevent the camera going under the floor
    camera.onViewMatrixChangedObservable.add(
        camera =>
            (camera.position =
                camera.position.y < 0.1
                    ? new BABYLON.Vector3(camera.position.x, 0.01, camera.position.z)
                    : camera.position),
    );

    // absolutely horrifying camera input tracking event after 1s of no interaction
    let timer: any;
    let cameraLoaded = false;
    const debounceUserInteraction = () => {
        clearTimeout(timer);
        timer = setTimeout(onSceneUserInteraction, 1000);
    };
    camera.onViewMatrixChangedObservable.add(_ => {
        cameraLoaded && debounceUserInteraction();
        cameraLoaded = true;
    });

    camera.fov = BABYLON.Angle.FromDegrees(50).radians();

    // if panToModelOnSelection is true, set up panning animation on mesh selection with revert to default on non selection
    let focusAnimation: BABYLON.Nullable<BABYLON.AnimationGroup> = null;
    panToModelOnSelection &&
        scene.onPointerObservable.add(pointerInfo => {
            // cancel on pan action
            if (
                pointerInfo.type === BABYLON.PointerEventTypes.POINTERDOWN &&
                pointerInfo.event.button == 2
            ) {
                focusAnimation?.stop();
            }

            if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERTAP) {
                const pickingInfo = scene.pick(
                    scene.pointerX,
                    scene.pointerY,
                    pickedMesh =>
                        pickedMesh.isPickable && !['ground', 'wall'].includes(pickedMesh.name),
                );
                const pickedMesh = pickingInfo?.pickedMesh;
                if (pickedMesh) {
                    const boundingSphere = pickedMesh.getBoundingInfo().boundingSphere;
                    focusAnimation = moveCameraTargetTo(
                        scene,
                        camera,
                        boundingSphere.centerWorld,
                        boundingSphere,
                        50,
                    );
                } else {
                    const rootTransform = scene.getTransformNodeByName(
                        ViewerTransformType.ROOT_CONFIGURATOR_TRANSFORM,
                    );
                    if (rootTransform) {
                        const boundingBox = rootTransform.getHierarchyBoundingVectors();
                        const boundingSphere = new BABYLON.BoundingSphere(
                            boundingBox.min,
                            boundingBox.max,
                        );
                        focusAnimation = moveCameraTargetTo(
                            scene,
                            camera,
                            startPosition,
                            boundingSphere,
                            50,
                        );
                    }
                }
            }
        });

    return camera;
};

export const createPBRSkybox = (scene: BABYLON.Scene) => {
    const environmentTexture = BABYLON.CubeTexture.CreateFromPrefilteredData(
        'assets/studio.env',
        scene,
    );

    // use the skybox for the lighting only, but rely on a clear color for the background instead
    scene.clearColor = BABYLON.Color4.FromHexString('#EEF2FF');

    // set the image processing which works best with this environment map
    scene.imageProcessingConfiguration.toneMappingEnabled = true;
    scene.imageProcessingConfiguration.toneMappingType =
        BABYLON.ImageProcessingConfiguration.TONEMAPPING_ACES;
    scene.imageProcessingConfiguration.contrast = 1;
    scene.imageProcessingConfiguration.exposure = 1;

    environmentTexture.rotationY = 1.77;
    const skyboxMesh = scene.createDefaultSkybox(environmentTexture, true, 1000, 0.5, true);
    if (skyboxMesh) {
        skyboxMesh.visibility = 0;
    }
    return skyboxMesh;
};

export const createGuiLayer = (scene: BABYLON.Scene) =>
    GUI.AdvancedDynamicTexture.CreateFullscreenUI('guiLayer', true, scene);

export const createInteractionHint = (
    scene: BABYLON.Scene,
    gui: GUI.AdvancedDynamicTexture,
    camera: BABYLON.ArcRotateCamera,
) => {
    // TODO: Tidy camera interaction hint experiment setup (TDC-1118)
    // interaction hint icon
    const hintIcon = new GUI.Image('interactionHint', 'assets/camera-nudge-hint.svg');
    hintIcon.width = '96px';
    hintIcon.height = '96px';
    hintIcon.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
    hintIcon.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_CENTER;
    hintIcon.alpha = 0;
    gui.addControl(hintIcon);

    const hintLength = 180;

    let active = false;

    let currentHintIconSwipeAnim: BABYLON.Animatable;
    let currentCameraSwipeAnim: BABYLON.Animatable;

    // set up keys for all animations
    const iconFadeKeys = [
        { frame: 0, value: 0.0 },
        { frame: 20, value: 1.0 },
        { frame: hintLength - 20, value: 1.0 },
        { frame: hintLength, value: 0.0 },
    ];

    const iconSwipeKeys = [
        { frame: 0, value: 0.0 },
        { frame: Math.floor(hintLength * 0.3), value: -70.0 },
        { frame: Math.floor(hintLength * 0.7), value: 70.0 },
        { frame: hintLength, value: 0.0 },
    ];

    // set up easing function and easing mode
    const easingFunction = new BABYLON.CubicEase();
    const easingMode = BABYLON.EasingFunction.EASINGMODE_EASEINOUT;
    easingFunction.setEasingMode(easingMode);

    // create animation clips
    const iconFadeAnim = new BABYLON.Animation(
        'iconFade',
        'alpha',
        60,
        BABYLON.Animation.ANIMATIONTYPE_FLOAT,
        BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    const iconSwipeAnim = new BABYLON.Animation(
        'iconSwipe',
        'left',
        60,
        BABYLON.Animation.ANIMATIONTYPE_FLOAT,
        BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT,
    );

    // set up easing
    iconFadeAnim.setEasingFunction(easingFunction);
    iconSwipeAnim.setEasingFunction(easingFunction);

    // append keys to animation
    iconFadeAnim.setKeys(iconFadeKeys);
    iconSwipeAnim.setKeys(iconSwipeKeys);

    function play() {
        // set up keys for camera rotation starting at current camera.alpha
        const cameraSwipeKeys = [
            { frame: 0, value: camera.alpha },
            { frame: Math.floor(hintLength * 0.3), value: camera.alpha + 0.4 },
            { frame: Math.floor(hintLength * 0.7), value: camera.alpha - 0.4 },
            { frame: hintLength, value: camera.alpha },
        ];

        // set up animation and easing for camera swipe
        const cameraSwipeAnim = new BABYLON.Animation(
            'cameraSwipe',
            'alpha',
            60,
            BABYLON.Animation.ANIMATIONTYPE_FLOAT,
            BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT,
        );
        cameraSwipeAnim.setEasingFunction(easingFunction);

        // append keys to animation
        cameraSwipeAnim.setKeys(cameraSwipeKeys);

        // play animations
        currentCameraSwipeAnim = scene.beginDirectAnimation(
            camera,
            [cameraSwipeAnim],
            0,
            hintLength,
            false,
            1,
        );
        currentHintIconSwipeAnim = scene.beginDirectAnimation(
            hintIcon,
            [iconFadeAnim, iconSwipeAnim],
            0,
            hintLength,
            false,
            1,
            () => {
                active = false;
            },
        );
        active = true;
    }

    function cancel() {
        if (active) {
            //stop current animations
            currentHintIconSwipeAnim.stop();
            currentCameraSwipeAnim.stop();

            // set up dehydrate keys
            const iconFadeoutKeys = [
                { frame: 0, value: hintIcon.alpha },
                { frame: 10, value: 0.0 },
            ];

            // create dehydrate animation and add keys
            const iconFadeoutAnim = new BABYLON.Animation(
                'hintIconFadeout',
                'alpha',
                60,
                BABYLON.Animation.ANIMATIONTYPE_FLOAT,
                BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT,
            );
            iconFadeoutAnim.setKeys(iconFadeoutKeys);

            // play animation
            scene.beginDirectAnimation(
                hintIcon,
                [iconFadeoutAnim],
                0,
                iconFadeoutKeys[iconFadeoutKeys.length - 1].frame,
                false,
                1,
                () => {
                    active = false;
                },
            );
        }
    }
    return { play, cancel };
};

function moveCameraTargetTo(
    scene: BABYLON.Scene,
    camera: BABYLON.ArcRotateCamera,
    targetPosition: BABYLON.Vector3,
    fitToboundingSphere: BABYLON.BoundingSphere,
    speed: number,
): BABYLON.AnimationGroup {
    // if the screen is taller than it is wide (aspect ratio < 1), constrain it to the width by multiplying by the ratio
    const aspectRatio = scene.getEngine().getAspectRatio(camera);
    const aspectMultiplier = Math.min(1, aspectRatio);

    // get the distance in meters required to fit the bounding sphere (with a + 50% padding border)
    const targetCameraDistance =
        (fitToboundingSphere.radius * 1.5) / (Math.tan(camera.fov / 2) * aspectMultiplier);

    // build pan and zoom animations
    const ease = new BABYLON.CubicEase();
    ease.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEINOUT);

    const panAnimation = new BABYLON.Animation(
        'panCameraAnimation',
        'target',
        speed,
        BABYLON.Animation.ANIMATIONTYPE_VECTOR3,
        BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    panAnimation.setKeys([
        { frame: 0, value: camera.target },
        { frame: 60, value: targetPosition },
    ]);
    panAnimation.setEasingFunction(ease);

    const zoomAnimation = new BABYLON.Animation(
        'zoomCameraAnimation',
        'radius',
        speed,
        BABYLON.Animation.ANIMATIONTYPE_FLOAT,
        BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    zoomAnimation.setKeys([
        { frame: 0, value: camera.radius },
        { frame: 60, value: targetCameraDistance },
    ]);
    zoomAnimation.setEasingFunction(ease);

    // group, play and return both animations
    const group = new BABYLON.AnimationGroup('focusCameraAnimationGroup', scene);
    group.addTargetedAnimation(panAnimation, camera);
    group.addTargetedAnimation(zoomAnimation, camera);
    return group.play();
}
